import darkTheme from 'config/themes/dark'
import defaultTheme from 'config/themes/default'
import lightTheme from 'config/themes/light'
import { getWidgetColorMode } from 'modules/embed/selectors'
import { keys,memoizeWith, pick } from 'ramda'
import { CSSProperties, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EmbedTheme } from 'types/embed'
import { WidgetColorMode, WidgetColorTheme } from 'types/theme'

/**
 * Will attempt to resolve a key value from the Embed theme (i.e. values set in design settings)
 * If one is not found, it will fallback to the default theme at config/themes/default
 * @param theme - EmbedTheme object
 * @param key - key to lookup in both the EmbedTheme and WidgetColorTheme object
 * @returns a string value for the found property
 */
const resolveFromEmbedThemeOrDefault = (theme: EmbedTheme, key: string): string => theme[key] || defaultTheme[key]

const toColorValue = (value: string): string => {
  if (!value) {
    return ''
  }

  const isHexValue = value.match(/[0-9A-Fa-f]{6}/g)

  if (!isHexValue) {
    return value
  }

  return value.indexOf('#') === 0 ? value : `#${value}`
}

const isDefaultColorMode = (colorMode: WidgetColorMode) => colorMode === WidgetColorMode.DEFAULT

const resolveBackgroundStyles = (theme: EmbedTheme): CSSProperties => {
  const backgroundColor = resolveFromEmbedThemeOrDefault(theme, 'backgroundColor')
  const textColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  return {
    background: toColorValue(backgroundColor),
    color: toColorValue(textColor)
  }
}

const resolveSvgStyles = (theme: EmbedTheme, themeColors: WidgetColorTheme, colorMode: WidgetColorMode): CSSProperties => {
  const fillColor = isDefaultColorMode(colorMode)
    ? resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')
    : themeColors.agentMessageTextColor

  return {
    fill: toColorValue(fillColor)
  }
}

const resolveBorderStyles = (theme: EmbedTheme): CSSProperties => {
  const borderColor = resolveFromEmbedThemeOrDefault(theme, 'backgroundColor')

  return {
    borderColor: toColorValue(borderColor)
  }
}

const resolveTextColorStyles = (theme: EmbedTheme): CSSProperties => {
  const textColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  return {
    color: toColorValue(textColor)
  }
}

const resolveInlineImageStyles = (theme: EmbedTheme): CSSProperties => {
  const foregroundColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  return {
    background: toColorValue(foregroundColor),
    border: `4px solid ${toColorValue(foregroundColor)}`
  }
}

const resolveHeaderTextStyles = (theme: EmbedTheme): CSSProperties => {
  const headerColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  return {
    color: toColorValue(headerColor)
  }
}

const resolveInlineFileStyles = (theme: EmbedTheme): CSSProperties => {
  const inlineFileColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  return {
    color: toColorValue(inlineFileColor)
  }
}

const resolveButtonStyles = (theme: EmbedTheme, themeColors: WidgetColorTheme, colorMode: WidgetColorMode): CSSProperties => {
  const embedBackgroundColor = resolveFromEmbedThemeOrDefault(theme, 'backgroundColor')
  const embedForegroundColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  const backgroundColor = isDefaultColorMode(colorMode) ? embedForegroundColor : themeColors.buttonBackgroundColor
  const textColor = isDefaultColorMode(colorMode) ? embedBackgroundColor : themeColors.buttonTextColor

  return {
    color: toColorValue(textColor),
    borderColor: toColorValue(textColor),
    backgroundColor: toColorValue(backgroundColor)
  }
}

const resolveForegroundColors = (theme: EmbedTheme): CSSProperties => {
  const backgroundColor = resolveFromEmbedThemeOrDefault(theme, 'backgroundColor')
  const foregroundColor = resolveFromEmbedThemeOrDefault(theme, 'foregroundColor')

  return {
    backgroundColor: toColorValue(backgroundColor),
    color: toColorValue(foregroundColor),
    border: `1px solid ${toColorValue(backgroundColor)}`
  }
}

const resolvePrimaryTextColor = (theme: EmbedTheme): CSSProperties => {
  const primaryTextColor = resolveFromEmbedThemeOrDefault(theme, 'backgroundColor')

  return {
    color: toColorValue(primaryTextColor)
  }
}

const resolveWidgetBackgroundStyles = (theme: EmbedTheme): CSSProperties => {
  const widgetBackgroundColor = resolveFromEmbedThemeOrDefault(theme, 'widgetBackgroundColor')

  return {
    background: toColorValue(widgetBackgroundColor)
  }
}

const resolveWidgetAlphaBackground = (theme: EmbedTheme, alpha = '.8'): CSSProperties => {
  const convertHex = (hex) => {
    const r = parseInt(hex.substring(0,2), 16)
    const g = parseInt(hex.substring(2,4), 16)
    const b = parseInt(hex.substring(4,6), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  const widgetBackgroundColor = resolveFromEmbedThemeOrDefault(theme, 'widgetBackgroundColor')

  return {
    background: convertHex(widgetBackgroundColor)
  }
}

const resolveDefaultBotAvatarBackground = (theme: EmbedTheme): CSSProperties => {
  const backgroundColor = resolveFromEmbedThemeOrDefault(theme, 'backgroundColor')

  return {
    background: toColorValue(backgroundColor)
  }
}

const resolveFooterTextColor = (themeColors: WidgetColorTheme): CSSProperties => {
  return {
    color: themeColors.footerText
  }
}

const resolveComposerButtonHoverColor = (themeColors: WidgetColorTheme): string => {
  return themeColors.composerIconHoverColor
}

const resolveComposerIconColor = (themeColors: WidgetColorTheme): string => {
  return themeColors.composerIconColor
}

const resolveComposerTheme = (themeColors: WidgetColorTheme): CSSProperties => {
  return {
    color: themeColors.composerTextColor,
    borderColor: themeColors.composerBorderColor,
    background: themeColors.composerBackgroundColor
  }
}

const resolveEndUserMessageTheme = (theme: EmbedTheme, themeColors: WidgetColorTheme, colorMode: WidgetColorMode): CSSProperties => {
  const backgroundStyles = resolveBackgroundStyles(theme)
  const textColorStyles = resolveTextColorStyles(theme)

  const backgroundColor = isDefaultColorMode(colorMode) ? backgroundStyles.background : themeColors.endUserMessageBackgroundColor
  const textColor = isDefaultColorMode(colorMode) ? textColorStyles.color : themeColors.endUserMessageTextColor

  return {
    background: backgroundColor,
    color: textColor
  }
}

const resolveAgentMessageTheme = (themeColors: WidgetColorTheme) => {
  const backgroundColor = themeColors.agentMessageBackgroundColor
  const textColor = themeColors.agentMessageTextColor

  return {
    background: backgroundColor,
    color: textColor
  }
}

const resolveAgentIconTheme = (themeColors: WidgetColorTheme) => {
  const fillColor = themeColors.agentMessageTextColor

  return {
    fill: toColorValue(fillColor)
  }
}

const resolveMessageAuthorTheme = (themeColors: WidgetColorTheme): CSSProperties => {
  return {
    color: themeColors.messageAuthorColor
  }
}

const resolveMessageTimestampTheme = (themeColors: WidgetColorTheme): CSSProperties => {
  return {
    color: themeColors.messageTimestampColor
  }
}

const resolveConversationRatingHeaderTheme = (theme: EmbedTheme, themeColors: WidgetColorTheme, colorMode: WidgetColorMode): CSSProperties => {
  const backgroundStyles = resolveBackgroundStyles(theme)
  const textColorStyles = resolveTextColorStyles(theme)

  const backgroundColor = isDefaultColorMode(colorMode) ? backgroundStyles.background : themeColors.conversationRatingHeaderBackgroundColor
  const textColor = isDefaultColorMode(colorMode) ? textColorStyles.color : themeColors.conversationRatingHeaderTextColor

  return {
    background: backgroundColor,
    color: textColor
  }
}

const resolveConversationRatingBodyTheme = (themeColors: WidgetColorTheme) => {
  return {
    background: themeColors.conversationRatingBodyBackgroundColor
  }
}

const resolveConversationRatingCloseButtonTheme = (theme: EmbedTheme, themeColors: WidgetColorTheme, colorMode: WidgetColorMode): CSSProperties => {
  const textColor = isDefaultColorMode(colorMode)
    ? toColorValue(theme.foregroundColor)
    : themeColors.conversationRatingHeaderTextColor

  return {
    color: textColor
  }
}

const resolveCardHeaderTheme = (theme: EmbedTheme, themeColors: WidgetColorTheme, colorMode: WidgetColorMode) => {
  const embedBackgroundStyles = resolveBackgroundStyles(theme)
  const embedHeaderTextStyles = resolveHeaderTextStyles(theme)

  const backgroundColor = isDefaultColorMode(colorMode)
    ? embedBackgroundStyles.background
    : themeColors.agentMessageBackgroundColor

  const textColor = isDefaultColorMode(colorMode)
    ? embedHeaderTextStyles.color
    : themeColors.agentMessageTextColor

  return {
    background: backgroundColor,
    color: textColor
  }
}

const resolveGdprCopyTheme = (themeColors: WidgetColorTheme) => {
  return {
    color: themeColors.gdprTextColor
  }
}

const pickThemeColors = pick(['activeColor', 'foregroundColor', 'backgroundColor', 'widgetBackgroundColor'])
const memoIdentity = (theme: EmbedTheme, colorMode: WidgetColorTheme | {}) =>
  [...keys(pickThemeColors(theme)), ...keys(colorMode)].join(";")

const getThemeColors = memoizeWith(memoIdentity, (theme: EmbedTheme, themeColors: WidgetColorTheme | {}, colorMode: WidgetColorMode) => {
  return {
    /** Resolved via widget design settings in the embed */
    backgroundStyles: resolveBackgroundStyles(theme),
    borderStyles: resolveBorderStyles(theme),
    textColorStyles: resolveTextColorStyles(theme),
    foregroundStyles: resolveForegroundColors(theme),
    inlineImageStyles: resolveInlineImageStyles(theme),
    widgetBackgroundStyles: resolveWidgetBackgroundStyles(theme),
    conversationHeaderTextStyles: resolveHeaderTextStyles(theme),
    inlineFileStyles: resolveInlineFileStyles(theme),
    defaultBotAvatarBackgroundStyles: resolveDefaultBotAvatarBackground(theme),
    activeColor: theme.activeColor,
    widgetSecondaryColor: toColorValue(theme.foregroundColor),
    widgetBackgroundColor: theme.widgetBackgroundColor,
    widgetBackgroundAlpha: resolveWidgetAlphaBackground(theme),
    primaryTextColor: resolvePrimaryTextColor(theme),


    /** Theme colors are defined in config/themes */
    buttonStyles: resolveButtonStyles(theme, themeColors, colorMode),
    composerButtonIconColor: resolveComposerIconColor(themeColors),
    composerButtonHoverColor: resolveComposerButtonHoverColor(themeColors),
    footerTextColor: resolveFooterTextColor(themeColors),
    composerTheme: resolveComposerTheme(themeColors),
    messageAuthorTheme: resolveMessageAuthorTheme(themeColors),
    messageTimestampTheme: resolveMessageTimestampTheme(themeColors),
    endUserMessageTheme: resolveEndUserMessageTheme(theme, themeColors, colorMode),
    agentMessageTheme: resolveAgentMessageTheme(themeColors),
    agentIconTheme: resolveAgentIconTheme(themeColors),
    conversationRatingHeaderTheme: resolveConversationRatingHeaderTheme(theme, themeColors, colorMode),
    conversationRatingBodyTheme: resolveConversationRatingBodyTheme(themeColors),
    conversationRatingCloseButtonTheme: resolveConversationRatingCloseButtonTheme(theme, themeColors, colorMode),
    cardHeaderTheme: resolveCardHeaderTheme(theme, themeColors, colorMode),
    gdprCopyTheme: resolveGdprCopyTheme(themeColors),
    svgStyles: resolveSvgStyles(theme, themeColors, colorMode),
  }
})

/**
 * Resolves a series of custom CSS stylings via either widget design settings in-app or via colorMode (DEFAULT in chat mode, LIGHT/DARK/VIDEO in landing page mode)
 * @returns an object containing various style objects that can be applied to any JSX element
 */
const useThemeColors = () => {
  const embedTheme: EmbedTheme = useSelector(state => state.embed.configuration.theme)
  const colorMode = useSelector(getWidgetColorMode)
  const themeColors = useMemo(() => {
    switch(colorMode) {
      case WidgetColorMode.LIGHT:
        return lightTheme
      case WidgetColorMode.DARK:
        return darkTheme
      case WidgetColorMode.VIDEO:
      default:
        return defaultTheme
    }
  }, [colorMode])

  return getThemeColors(embedTheme, themeColors, colorMode)
}

export default useThemeColors
